// Several documents, one customer.
//
// An employee scans an ID and a licence, or a passport and a visit permit. Each one is
// read on its own by the engine; this is where they become a single set of fields.
//
// Three rules, in this order:
//   1. a value the employee typed always wins  (agreed: "manual edit wins")
//   2. a value from a machine zone whose check digits passed beats a value read visually
//   3. a later document never silently overwrites an earlier one: the disagreement is
//      recorded as a conflict so the employee sees both and picks
//
// Nothing here calls the network or touches a file; it is all pure, so it can be tested
// without an engine and without a document.

export const MANUAL = 'manual';
export const MRZ = 'mrz';
export const PRINTED = 'printed';
export const DERIVED = 'derived';

const RANK = { [MANUAL]: 4, [MRZ]: 3, [PRINTED]: 2, [DERIVED]: 1 };

// Both systems treat every Gulf national the same way, the UAE included.
export const GCC = ['ARE', 'SAU', 'KWT', 'BHR', 'OMN', 'QAT'];

// Everywhere in the Gulf except the UAE the licence number IS the identity number:
// the Saudi licence and the Saudi id in front of us both read 1098071333. The UAE is
// the exception - there the two numbers are unrelated and must never be copied across.
export const LICENCE_IS_ID = GCC.filter((c) => c !== 'ARE');

const clean = (v) => String(v ?? '').trim();
const bare = (v) => clean(v).toUpperCase().replace(/[^A-Z0-9]/g, '');
const sameText = (a, b) => bare(a) === bare(b);

// One nationality, several notations. A machine zone prints "EGY", a licence prints
// "Egypt", Ejar's own list says "Egyptian" - one fact in three spellings, and calling
// them a disagreement sent the employee hunting for a problem that did not exist.
const NATIONALITY_ALIASES = {
    ARE: ['ARE', 'UAE', 'UNITEDARABEMIRATES', 'EMARATI', 'EMIRATI'],
    SAU: ['SAU', 'KSA', 'SAUDIARABIA', 'SAUDI', 'KINGDOMOFSAUDIARABIA'],
    KWT: ['KWT', 'KUWAIT', 'KUWAITI'],
    BHR: ['BHR', 'BAHRAIN', 'BAHRAINI'],
    OMN: ['OMN', 'OMAN', 'OMANI'],
    QAT: ['QAT', 'QATAR', 'QATARI'],
    EGY: ['EGY', 'EGYPT', 'EGYPTIAN'],
    PAK: ['PAK', 'PAKISTAN', 'PAKISTANI'],
    IND: ['IND', 'INDIA', 'INDIAN'],
    PHL: ['PHL', 'PHILIPPINES', 'FILIPINO'],
    BGD: ['BGD', 'BANGLADESH', 'BANGLADESHI'],
    LKA: ['LKA', 'SRILANKA', 'SRILANKAN'],
    NPL: ['NPL', 'NEPAL', 'NEPALESE', 'NEPALI'],
    SYR: ['SYR', 'SYRIA', 'SYRIAN'],
    JOR: ['JOR', 'JORDAN', 'JORDANIAN'],
    LBN: ['LBN', 'LEBANON', 'LEBANESE'],
    SDN: ['SDN', 'SUDAN', 'SUDANESE'],
    YEM: ['YEM', 'YEMEN', 'YEMENI'],
    GBR: ['GBR', 'UK', 'UNITEDKINGDOM', 'BRITISH'],
    USA: ['USA', 'UNITEDSTATES', 'AMERICAN'],
    CAN: ['CAN', 'CANADA', 'CANADIAN'],
};

/** The ISO3 a nationality spelling stands for, or nothing when we do not know it. */
export const canonicalNationality = (value) => {
    const key = bare(value);
    if (!key) return null;
    for (const [iso3, spellings] of Object.entries(NATIONALITY_ALIASES)) {
        if (spellings.includes(key)) return iso3;
    }
    return null;
};

/** Do two readings of the same field say the same thing? */
const sameValue = (field, a, b) => {
    if (field === 'nationality') {
        const x = canonicalNationality(a);
        const y = canonicalNationality(b);
        if (x && y) return x === y;          // both known: compare the fact, not the spelling
    }
    return sameText(a, b);
};

/** One field as the card holds it: the value, where it came from, and how sure we are. */
const cell = (value, source, confidence, from) => ({
    value: clean(value), source, confidence: Number(confidence) || 0, from,
});

/**
 * What kind of document this is, which decides whose expiry date it carries.
 *
 * An identity card and a driving licence expire on different days, and treating
 * "expiry date" as one shared field made the two look like a contradiction: a real
 * customer's id ran to 2033 and his licence to 2029, and the card reported them as a
 * disagreement while quietly putting the id's date into the TARS licence-expiry box.
 */
export const documentKind = (doc) => {
    const layout = String(doc?.layout || '').toLowerCase();
    // Claude names the document itself (engine/automation/ocr/claude_reader.py, owner 10 Oct 2026)
    if (layout.includes('passport')) return 'passport';
    if (layout.includes('visa')) return 'visa';
    if (layout.includes('licence') || layout.includes('license')) return 'licence';
    if (layout.includes('id')) return 'id';

    const zone = doc?.mrz;
    if (zone) {
        const type = String(zone.document_type || '').toUpperCase();
        if (zone.format === 'TD3' || type.startsWith('P')) return 'passport';
        return 'id';
    }
    return null;
};

/**
 * Pull the fields we care about out of one engine result.
 * `index` is the document's position, so the card can say which file a value came from.
 */
export const fieldsOf = (doc, index = 0) => {
    const out = {};
    const label = doc?.filename || `document ${index + 1}`;
    const zone = doc?.mrz;

    // Trust is per field, decided by the check digit that actually covers it.
    //
    // Whole-zone trust fails both ways. Taking a failed zone at a lower rank let card
    // wording ("TRYOFINTE", "IORPRIVINGLICEN") argue with values that were right.
    // Dropping the whole zone instead threw away a real Saudi id whose only failure was
    // the document-number digit - its dates, its composite and its name were all sound.
    //
    // The composite digit covers the identity fields and the whole of line 2, so when it
    // passes the zone was read correctly even if one field's own digit did not.
    const checks = zone?.checks || {};
    const composite = checks.composite !== false;              // absent = not claimed
    const trust = (name) => !!zone && composite && checks[name] !== false;

    if (zone && composite) {
        const numbered = zone.document_number && trust('document_number');
        if (numbered) out.document_number = cell(zone.document_number, MRZ, 0.99, label);
        // Claude already gives the nationality in Ejar's own word, picked from Ejar's list ("Argentinean"), where
        // the zone only has the code (ARG) - the code would outrank it and the word Ejar needs would be lost.
        const said = clean(doc?.fields?.nationality?.value);
        const ejarWord = doc?.engine === 'claude' && said && !/^[A-Za-z]{3}$/.test(said);
        if (zone.nationality && !ejarWord) out.nationality = cell(zone.nationality, MRZ, 0.99, label);
        // Ejar demands a gender and the machine zone carries one, so there is no reason
        // to make the employee answer a question the document already answered.
        if (zone.sex === 'M' || zone.sex === 'F') {
            out.gender = cell(zone.sex === 'M' ? 'Male' : 'Female', MRZ, 0.99, label);
        }

        // Which field holds the identity number depends on the country. The UAE puts
        // its fifteen-digit Emirates ID in the optional data; everywhere else that
        // field is something local - on a Saudi card it held "2161", and taking it as
        // the identity number filed a real customer under a four-digit number while
        // the true one, 1098071333, sat in the document-number field.
        const optionalDigits = String(zone.optional_data || '').replace(/\D/g, '');
        if (optionalDigits.length === 15) {
            out.id_number = cell(zone.optional_data, MRZ, 0.99, label);
        } else if (numbered && documentKind(doc) !== 'passport') {
            // A passport's number is its own field (document_number), never the identity: beside an Emirates ID
            // it outranked the real identity number by its check digit and filed a resident under the passport.
            out.id_number = cell(zone.document_number, MRZ, 0.99, label);
        }
    }

    Object.entries(doc?.fields || {}).forEach(([name, f]) => {
        if (!f?.value) return;
        if (out[name] && RANK[out[name].source] >= RANK[PRINTED]) return;
        out[name] = cell(f.value, PRINTED, f.confidence, label);
    });

    // A birth date belongs to the person, so every document should agree on it. An
    // expiry or issue date belongs to the document, so each one keeps its own.
    const kind = documentKind(doc);
    Object.entries(doc?.dates || {}).forEach(([name, value]) => {
        if (!value) return;
        const fromZone = (name === 'birth_date' || name === 'expiry_date') && trust(name);
        const key = (name === 'birth_date' || !kind) ? name : `${kind}_${name.replace('_date', '')}`;
        out[key] = cell(value, fromZone ? MRZ : PRINTED, fromZone ? 0.99 : 0.9, label);
    });

    const person = doc?.name;
    // Line 3 carries no check digit of its own, so the composite is what vouches for it.
    if ((person?.surname || person?.given_names?.length)
        && !(person.source === 'mrz' && !composite)) {
        const rank = person.source === 'mrz' ? MRZ : PRINTED;
        const given = (person.given_names || []).join(' ');
        // An empty reading is not a competing answer. Emitting it anyway made a document
        // that simply could not read the name argue with one that could.
        if (clean(person.surname)) out.surname = cell(person.surname, rank, person.confidence, label);
        if (clean(given)) out.given_names = cell(given, rank, person.confidence, label);
    }
    return out;
};

/**
 * Merge every document into one set of fields, keeping the disagreements.
 * `manual` is whatever the employee has typed so far; it overrides everything.
 */
export const mergeDocuments = (docs = [], manual = {}) => {
    const fields = {};
    const conflicts = [];
    const warnings = [];

    docs.forEach((doc, i) => {
        if (!doc) return;
        const label = doc.filename || `document ${i + 1}`;
        (doc.warnings || []).forEach((w) => warnings.push({ from: label, text: w }));
        (doc.checks || []).forEach((c) => {
            if (!c.pass) warnings.push({ from: label, text: `${c.check}: ${c.detail}` });
        });

        Object.entries(fieldsOf(doc, i)).forEach(([name, next]) => {
            const current = fields[name];
            if (!current) { fields[name] = next; return; }
            if (sameValue(name, current.value, next.value)) {
                // Two documents agreeing is the strongest signal there is.
                fields[name] = next.confidence > current.confidence
                    ? { ...next, agreed: true } : { ...current, agreed: true };
                return;
            }
            const better = RANK[next.source] > RANK[current.source]
                || (RANK[next.source] === RANK[current.source] && next.confidence > current.confidence);
            conflicts.push({
                field: name,
                kept: better ? next.value : current.value,
                other: better ? current.value : next.value,
                keptFrom: better ? next.from : current.from,
                otherFrom: better ? current.from : next.from,
            });
            fields[name] = better ? next : current;
        });
    });

    // One number, two fields. Reading either one tells us the other, so whichever was
    // read fills the gap - marked `derived`, so the card says plainly that it was
    // concluded rather than read, and any real reading outranks it.
    const nat = clean(fields.nationality?.value).toUpperCase();
    if (LICENCE_IS_ID.some((c) => nat.startsWith(c))) {
        const id = fields.id_number;
        const licence = fields.licence_number;
        if (id?.value && !licence?.value) {
            fields.licence_number = cell(id.value, DERIVED, 0.9,
                `${nat}: the licence number is the identity number`);
        } else if (licence?.value && !id?.value) {
            fields.id_number = cell(licence.value, DERIVED, 0.9,
                `${nat}: the identity number is the licence number`);
        }
    }

    // A driving licence prints one line of name and carries no surname of its own, so
    // whatever is read as its "surname" is usually one of the given names. A passport
    // says ALI IBRAHIM and the licence says MOHAMED, and the card called that a
    // disagreement and refused to register a customer whose name was never in doubt.
    // Two readings only contradict each other when one of them names a word that is not
    // part of this person's name at all.
    const nameWords = new Set(
        `${fields.given_names?.value || ''} ${fields.surname?.value || ''}`
            .toLowerCase().split(/\s+/).filter(Boolean));
    const insideTheName = (text) => {
        const words = String(text || '').toLowerCase().split(/\s+/).filter(Boolean);
        return words.length > 0 && words.every((w) => nameWords.has(w));
    };
    // ...but once one document names a word that is not this person's name at all (ELAMIR beside MOHAMED ALI
    // IBRAHIM), the papers may be two different people: then every name disagreement stays in front of the employee,
    // not only the one that holds the stranger word.
    const isName = (c) => c.field === 'surname' || c.field === 'given_names';
    const stranger = conflicts.some((c) => isName(c)
        && !abbreviates(c.kept, c.other) && !abbreviates(c.other, c.kept)
        && !(insideTheName(c.kept) && insideTheName(c.other)));
    for (let i = conflicts.length - 1; i >= 0; i -= 1) {
        const c = conflicts[i];
        if (!isName(c)) continue;
        // A licence that shortens a name to its first letter says the same name: the Illinois licence's "DISHABEN J"
        // is the passport's DISHABEN JAGDISHBHAI (owner's test customer, 10 Oct 2026). Not a disagreement - and the
        // full spelling is the one kept, whichever document was dropped first.
        if (abbreviates(c.kept, c.other) || abbreviates(c.other, c.kept)) {
            const full = abbreviates(c.kept, c.other) ? c.other : c.kept;
            const from = full === c.other ? c.otherFrom : c.keptFrom;
            if (full !== c.kept) fields[c.field] = { ...fields[c.field], value: clean(full), from };
            conflicts.splice(i, 1);
        } else if (!stranger && insideTheName(c.kept) && insideTheName(c.other)) {
            conflicts.splice(i, 1);
        }
    }

    Object.entries(manual).forEach(([name, value]) => {
        if (clean(value) === '') return;
        fields[name] = cell(value, MANUAL, 1, 'typed by the employee');
    });

    return { fields, conflicts, warnings };
};

/**
 * Does `short` say the same name as `full`, only with some words cut to their first letter? "DISHABEN J" and
 * "DISHABEN JAGDISHBHAI" do, word for word in order; "DISHABEN K" or "DISHA JAGDISHBHAI" do not.
 */
export const abbreviates = (short, full) => {
    const a = clean(short).toUpperCase().split(/\s+/).filter(Boolean);
    const b = clean(full).toUpperCase().split(/\s+/).filter(Boolean);
    if (!a.length || a.length !== b.length || a.join(' ') === b.join(' ')) return false;
    return a.every((w, i) => {
        const bare = w.replace(/\.$/, '');
        return bare === b[i] || (bare.length === 1 && b[i].startsWith(bare));
    });
};

const valueOf = (fields, name) => clean(fields?.[name]?.value);

/**
 * MOHAMED -> Mohamed. A machine zone is all capitals by design, and both systems end up
 * holding a customer's name shouted at them.
 *
 * The word boundary is not only a space: AL-SHARIF must become Al-Sharif and O'BRIEN
 * O'Brien, which a plain "capitalise after a space" gets wrong in a name people will
 * see on a contract.
 */
export const titleCase = (value) => clean(value).toLowerCase()
    .replace(/(^|[\s\-'’.])([a-z؀-ۿ])/g, (_, before, letter) => before + letter.toUpperCase());

/**
 * GCC national, resident or tourist. `kinds` = the kinds of the documents read (documentKind): a customer with a
 * passport and no identity card is a TOURIST - filed on Ejar under the passport (251) issued by their own country,
 * not as a UAE residence (owner's test customer, 10 Oct 2026: an Indian passport went to Ejar as "Residence").
 * Without `kinds` the old two-way answer stands.
 */
export const classify = (fields, kinds = null) => {
    const nationality = valueOf(fields, 'nationality').toUpperCase();
    if (!nationality) return { known: false };
    const iso = canonicalNationality(nationality);
    const gcc = GCC.some((c) => iso === c || nationality.startsWith(c));
    if (kinds && kinds.has('passport') && !kinds.has('id') && !gcc) {
        return {
            known: true, gcc: false, tourist: true,
            ejarDocumentType: 251, ejarDocumentLabel: 'Passport', tarsCustomerType: 'Tourist',
        };
    }
    return {
        known: true,
        gcc,
        ejarDocumentType: gcc ? 250 : 252,          // Identity / Residence
        ejarDocumentLabel: gcc ? 'Identity' : 'Residence',
        tarsCustomerType: gcc ? 'GCC National' : 'Resident',
    };
};

/**
 * Ejar keeps five name parts, and the family name is one word.
 *
 * A passport's surname is whatever the issuing country put there, and for an Egyptian
 * that is often two words - ALI IBRAHIM. Filing that whole string as the family name
 * leaves the third and fourth boxes empty and the family box wrong; only the last word
 * is the family. So the given names and the surname are read as one line of words: the
 * last word is the family, and everything before it fills first, second, third, fourth.
 */
export const nameParts = (given, surname) => {
    const words = `${given} ${surname}`.split(/\s+/).filter(Boolean);
    const family = words.length > 1 ? words[words.length - 1] : '';
    const rest = words.length > 1 ? words.slice(0, -1) : words;
    const [first = '', second = '', third = '', ...spill] = rest;
    return { first, second, third, fourth: spill.join(' '), family };
};

export const toEjar = (fields, kinds = null) => {
    const parts = nameParts(valueOf(fields, 'given_names'), valueOf(fields, 'surname'));
    const kind = classify(fields, kinds);
    const passport = kind.ejarDocumentType === 251;
    return {
        first_name: titleCase(parts.first),
        second_name: titleCase(parts.second),
        third_name: titleCase(parts.third),
        fourth_name: titleCase(parts.fourth),
        family_name: titleCase(parts.family),
        birth_date: valueOf(fields, 'birth_date'),
        gender: valueOf(fields, 'gender'),
        nationality: valueOf(fields, 'nationality'),
        identity_number: passport
            ? (valueOf(fields, 'document_number') || valueOf(fields, 'id_number'))
            : (valueOf(fields, 'id_number') || valueOf(fields, 'document_number')),
        document_type: kind.known ? kind.ejarDocumentType : null,
        // the country printed on the document filed, and the licence's own (an Illinois licence = the USA)
        document_country: passport ? valueOf(fields, 'passport_country') : valueOf(fields, 'id_country'),
        // Ejar's settings require a visa number with a passport (IsVisaNumberRequiredForPassport)
        visa_number: valueOf(fields, 'visa_number'),
        // The expiry of the document this profile is being filed under, not whichever
        // expiry happened to be read last.
        document_expiry: kind.ejarDocumentType === 251
            ? (valueOf(fields, 'passport_expiry') || valueOf(fields, 'expiry_date'))
            : (valueOf(fields, 'id_expiry') || valueOf(fields, 'expiry_date')),
        // Ejar's Documents grid has an Issue Date column of its own.
        document_issue: kind.ejarDocumentType === 251
            ? (valueOf(fields, 'passport_issue') || valueOf(fields, 'issue_date'))
            : (valueOf(fields, 'id_issue') || valueOf(fields, 'issue_date')),
        // The licence is a document of its own in Ejar, filed beside the identity. A
        // profile without it is short of the paper the car is actually rented against.
        licence_number: valueOf(fields, 'licence_number'),
        licence_expiry: valueOf(fields, 'licence_expiry'),
        licence_country: valueOf(fields, 'licence_country'),
        // No document carries these two, and Ejar will not open a profile without them.
        phone: valueOf(fields, 'phone'),
        email: valueOf(fields, 'email'),
    };
};

// What Ejar will not accept without, named the way the panel names it.
const REQUIRED = [
    ['first_name', 'first name'],
    ['family_name', 'family name'],
    ['birth_date', 'date of birth'],
    ['gender', 'gender'],
    ['nationality', 'nationality'],
    ['identity_number', 'identity number'],
    ['phone', 'phone'],          // never on a document - the employee asks the customer
    ['email', 'email'],
];

/**
 * What still has to happen before anything may be written.
 *
 * `final` is what the panel will actually send - the read with the employee's
 * corrections on top. Checking the raw read instead kept saying "nationality is still
 * empty" after they had just typed it in, which teaches people to ignore the list.
 */
// Which merged field an Ejar box stands for, so that typing a family name counts as
// settling the surname the documents argued about.
export const FIELD_BEHIND = {
    first_name: 'given_names', second_name: 'given_names', third_name: 'given_names',
    fourth_name: 'given_names', family_name: 'surname',
    birth_date: 'birth_date', nationality: 'nationality',
    identity_number: 'id_number', document_expiry: 'id_expiry',
};

/** The merged fields the employee has answered for themselves. */
export const settled = (manual = {}, panelEdits = {}) => new Set([
    ...Object.keys(manual),
    ...Object.keys(panelEdits).map((k) => FIELD_BEHIND[k] || k),
]);

export const blockers = ({ fields, conflicts, warnings }, final = null, notesApart = false,
                         decided = new Set()) => {
    const missing = final
        ? REQUIRED.filter(([key]) => !clean(final[key])).map(([, label]) => label)
        : ['given_names', 'surname', 'birth_date', 'nationality']
            .filter((name) => !valueOf(fields, name)).map((n) => n.replace(/_/g, ' '));
    return [
        ...missing.map((label) => `${label} is still empty`),
        // A disagreement the employee has already answered is not something to fix.
        // Leaving it listed after they had typed the right value themselves is how the
        // list starts being ignored.
        ...conflicts.filter((c) => !decided.has(c.field))
            .map((c) => `${c.field.replace(/_/g, ' ')}: "${c.kept}" and "${c.other}" disagree`),
        ...(notesApart ? [] : warnings.map((w) => w.text)),
    ];
};

/**
 * What the documents said about themselves, as opposed to what still has to be fixed.
 *
 * A note is not a blocker. The Saudi licence really has nothing readable on its back,
 * and the id's printed expiry really did come out as "7 .4 44/1 (6" - but the expiry
 * arrived from the machine zone on the other side anyway, so the case is complete.
 * Leaving those in the red list next to "nationality is still empty" is how a warning
 * list stops being read at all.
 */
export const notes = ({ warnings, conflicts = [] }, decided = new Set()) => [
    ...warnings.map((w) => ({ from: w.from, text: w.text })),
    // Kept, but out of the red box: the employee settled it, and the record of what the
    // documents actually said is still worth having.
    ...conflicts.filter((c) => decided.has(c.field)).map((c) => ({
        from: 'you settled this',
        text: `${c.field.replace(/_/g, ' ')}: the documents said "${c.kept}" and "${c.other}"`,
    })),
];
